import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { istSichtbar } from "../bedingung";
import cfg from "../../config";
import "./css/vorschau.css";

/**
 * Zeigt ein Formular so, wie es beim Ausfuellen aussieht - und laesst sich
 * bedienen. Nur so laesst sich pruefen, ob die Bedingungen greifen: Typ
 * waehlen und sehen, ob die richtigen Felder erscheinen.
 *
 * Drei Stellen fuellt beim echten Formular erst der Server: die Typauswahl,
 * die Fragen des gewaehlten Typs und die bestellbaren Teile. Reicht der
 * Aufrufer sie mit, sind sie hier genauso bedienbar wie alles andere;
 * fehlen sie, steht an ihrer Stelle ein Hinweis.
 *
 * Eingaben bleiben hier und werden nirgends gespeichert.
 */
const AdminFormVorschau = ({ felder, titel, offen, onSchliessen, nichtGespeichert }) => {
    const [werte, setWerte] = useState({});

    // Gegen eine fehlende oder kaputte Liste absichern: ein Fehler beim
    // Aufbau wuerde den Dialog leer lassen, waehrend der graue Hintergrund
    // stehen bleibt - fuer den Anwender sieht das aus, als ginge gar nichts
    // auf.
    const sichere = Array.isArray(felder) ? felder.filter(f => f && typeof f === 'object') : [];

    // Die Typauswahl steuert, welche Fragen und welche Teile gelten.
    const typFeld = sichere.find(f => f.type === "costume-type-select");
    const typOptionen = typFeld ? (typFeld.options || []) : [];
    const typName = typFeld ? typFeld.name : null;
    const gewaehlterTyp = typName ? (werte[typName] || "") : "";

    /**
     * Eine Eingabe uebernehmen - und alles vergessen, was dadurch unsichtbar
     * wird. Ohne das bliebe die Schuhgroesse stehen, wenn jemand den Typ
     * wechselt, und ein daran haengendes Feld bliebe sichtbar, obwohl sein
     * Ausloeser verschwunden ist.
     */
    const setzen = (name, wert) => setWerte(vorher => {
        const naechste = { ...vorher, [name]: wert };
        let nochmal = true;
        while (nochmal) {
            nochmal = false;
            for (const f of sichere) {
                if (!f.name || f.name === name) continue;
                if (naechste[f.name] === undefined) continue;
                if (istSichtbar(f, naechste)) continue;
                delete naechste[f.name];
                nochmal = true;
            }
        }
        return naechste;
    });

    // Steht nur ein Typ zur Wahl, ist er gemeint: die Vorschau eines Typs
    // zeigt genau diesen einen. Ihn erst anklicken zu muessen, waere ein
    // leerer Schritt.
    const einzigerTyp = typOptionen.length === 1 ? typOptionen[0].value : null;
    useEffect(() => {
        if (!offen || !typName || einzigerTyp === null) return;
        setWerte(vorher => (vorher[typName] ? vorher : { ...vorher, [typName]: einzigerTyp }));
    }, [offen, typName, einzigerTyp]);

    // Dieselbe Pruefung wie im echten Formular - sonst zeigt die Vorschau
    // etwas anderes, als der Antragsteller spaeter sieht.
    const sichtbar = (feld) => istSichtbar(feld, werte);

    // Die Fragen des Typs stehen im ausgelieferten Schema hinter dem
    // Platzhalter. Sind sie da, ist der Platzhalter selbst ueberfluessig.
    const hatTypfragen = sichere.some(f => f._verwaltet);

    const zeigbar = sichere.filter(f => {
        if (!sichtbar(f)) return false;
        if (f.type === "type-questions" && hatTypfragen && gewaehlterTyp) return false;
        return true;
    });

    const eingabe = (feld) => {
        const wert = werte[feld.name];
        const kennung = `vorschau-${feld.name}`;
        const gemeinsam = {
            className: "form-control",
            id: kennung,
            value: wert ?? "",
            onChange: (e) => setzen(feld.name, e.target.value),
        };

        switch (feld.type) {
            case "checkbox":
                return (
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id={kennung}
                            checked={!!wert}
                            onChange={(e) => setzen(feld.name, e.target.checked)} />
                        <label className="form-check-label" htmlFor={kennung}>
                            {feld.label}
                            {feld.required && <span className="text-danger ms-1">*</span>}
                        </label>
                    </div>
                );
            case "textarea":
                return <textarea {...gemeinsam} rows={3} />;
            case "select":
                return (
                    <select className="form-select" id={kennung}
                        value={wert ?? ""} onChange={(e) => setzen(feld.name, e.target.value)}>
                        <option value="">-- bitte auswählen --</option>
                        {(feld.options || []).map((o, i) => (
                            <option key={i} value={o.value}>{o["frontend-text"] ?? o.value}</option>
                        ))}
                    </select>
                );
            case "multiselect":
                return (
                    <select className="form-select" id={kennung} multiple
                        value={Array.isArray(wert) ? wert : []}
                        onChange={(e) => setzen(feld.name,
                            [...e.target.selectedOptions].map(o => o.value))}>
                        {(feld.options || []).map((o, i) => (
                            <option key={i} value={o.value}>{o["frontend-text"] ?? o.value}</option>
                        ))}
                    </select>
                );
            // Die Typauswahl ist das Herz des Antrags: an ihr haengen die
            // typabhaengigen Felder. Als gesperrtes Feld liess sich in der
            // Vorschau nichts davon ausprobieren.
            case "costume-type-select":
                if (typOptionen.length === 0) {
                    return (
                        <select className="form-select" id={kennung} disabled>
                            <option>— wird aus der Typenliste gefüllt —</option>
                        </select>
                    );
                }
                return (
                    <select className="form-select" id={kennung}
                        value={wert ?? ""} onChange={(e) => setzen(feld.name, e.target.value)}>
                        <option value="">-- bitte auswählen --</option>
                        {typOptionen.map((o, i) => (
                            <option key={i} value={o.value}>{o["frontend-text"] ?? o.value}</option>
                        ))}
                    </select>
                );
            case "section":
                return bestellbareTeile(feld);
            case "type-questions":
                return (
                    <div className="vorschau-platzhalter">
                        {hatTypfragen
                            ? `Wähle oben einen ${cfg.costumeTerm}typ — dann erscheinen hier seine Felder.`
                            : "Hier folgen die Felder des gewählten Typs."}
                    </div>
                );
            case "label":
                return <div className="vorschau-platzhalter">Nur ein Text im Formular.</div>;
            case "number":
                return <input {...gemeinsam} type="number" />;
            case "date":
                return <input {...gemeinsam} type="date" />;
            case "time":
                return <input {...gemeinsam} type="time" />;
            default:
                return <input {...gemeinsam} type="text" />;
        }
    };

    /**
     * Die bestellbaren Teile eines Typs. Jedes Teil ist ein Haekchen; was
     * daran haengt, erscheint erst, wenn es gesetzt ist - genau wie auf der
     * Bestellseite.
     */
    const bestellbareTeile = (feld) => {
        if (!Array.isArray(feld.dynamicItems)) {
            return <div className="vorschau-platzhalter">Hier stehen die bestellbaren Teile dieses Typs.</div>;
        }
        if (typFeld && !gewaehlterTyp) {
            return (
                <div className="vorschau-platzhalter">
                    Wähle oben einen {cfg.costumeTerm}typ — dann erscheinen die bestellbaren Teile.
                </div>
            );
        }
        const gilt = (teil) => !gewaehlterTyp
            || !Array.isArray(teil.allowedFor) || teil.allowedFor.length === 0
            || teil.allowedFor.some(n => String(n).toLowerCase() === gewaehlterTyp.toLowerCase());

        const teile = feld.dynamicItems.filter(gilt);
        if (teile.length === 0) {
            return <div className="vorschau-platzhalter">Für diesen Typ ist kein Teil hinterlegt.</div>;
        }
        return teile.map((teil, i) => (
            <div className="vorschau-teil" key={`${teil.name}-${i}`}>
                <div className="form-check">
                    <input className="form-check-input" type="checkbox" id={`vorschau-${teil.name}`}
                        checked={!!werte[teil.name]}
                        onChange={(e) => setzen(teil.name, e.target.checked)} />
                    <label className="form-check-label" htmlFor={`vorschau-${teil.name}`}>
                        {teil.displayName || teil.label || teil.name}
                    </label>
                </div>
                {werte[teil.name] && (teil.fields || []).map((unter, j) => (
                    <div className="vorschau-unterfeld" key={`${unter.name}-${j}`}>
                        {unter.type !== "checkbox" && (
                            <label className="vorschau-beschriftung" htmlFor={`vorschau-${unter.name}`}>
                                {unter.label}
                                {unter.required && <span className="vorschau-pflicht">*</span>}
                            </label>
                        )}
                        {eingabe(unter)}
                    </div>
                ))}
            </div>
        ));
    };

    // Nur die Felder zaehlen, die eine Bedingung zurueckhaelt. Der
    // Platzhalter der Typfragen ist keins davon - er verschwindet, weil die
    // Fragen selbst an seiner Stelle stehen.
    const versteckt = sichere.filter(f => !sichtbar(f)).length;

    return (
        // data-bs-theme sorgt dafuer, dass Felder und Beschriftungen zur
        // dunklen Oberflaeche passen. Ohne das leuchteten weisse Eingaben im
        // dunklen Dialog, und die Beschriftungen waren kaum zu lesen.
        <Modal show={!!offen} onHide={onSchliessen} size="lg" scrollable centered
            data-bs-theme="dark" className="vorschau-fenster">
            <Modal.Header closeButton>
                <Modal.Title as="h5">
                    {titel || "Vorschau"}
                    <span className="vorschau-marke">Vorschau</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Die Vorschau zeigt den Bearbeitungsstand, das echte
                    Formular den gespeicherten. Ohne diesen Hinweis sieht es
                    aus, als kaeme die Aenderung nicht an. */}
                {nichtGespeichert ? (
                    <div className="alert alert-warning py-2">
                        <strong>Noch nicht gespeichert.</strong> Du siehst deinen aktuellen
                        Bearbeitungsstand. Im echten Formular erscheint er erst,
                        nachdem du auf „Speichern" geklickt hast.
                    </div>
                ) : (
                    <p className="vorschau-hinweis">
                        So sieht das Formular beim Ausfüllen aus. Probiere ruhig aus,
                        was passiert — nichts davon wird gespeichert.
                    </p>
                )}

                {zeigbar.length === 0 && (
                    <p className="vorschau-hinweis mb-0">Dieses Formular hat noch keine Felder.</p>
                )}

                <div className="vorschau-blatt">
                    {zeigbar.map((feld, i) => (
                        <div className="vorschau-feld" key={`${feld.name}-${i}`}>
                            {feld.type !== "checkbox" && (
                                <label className="vorschau-beschriftung" htmlFor={`vorschau-${feld.name}`}>
                                    {feld.label}
                                    {feld.required && <span className="vorschau-pflicht">*</span>}
                                </label>
                            )}
                            {eingabe(feld)}
                        </div>
                    ))}
                </div>

                {versteckt > 0 && (
                    <p className="vorschau-versteckt mb-0">
                        {versteckt === 1
                            ? "Ein weiteres Feld erscheint, sobald seine Bedingung zutrifft."
                            : `${versteckt} weitere Felder erscheinen, sobald ihre Bedingung zutrifft.`}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onSchliessen}>Schließen</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AdminFormVorschau;
